import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { ModalLiberacaoPadrao } from "./components/ModalLiberacaoPadrao";
import { useAuth } from "shared/hooks/auth";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";
import { apiAntigo, irParaLogin } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

interface ILiberacaoPadrao {
  id: number;
  nomeLiberacao: string;
  sistema: string;
  patrocinio: string;
}

type FilterKey = "nomeLiberacao" | "sistema" | "patrocinio";

interface IPatrocinio {
  idPatrocinio: number;
  descricao: string;
}

export const LiberacoesPadrao = () => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();
  const navigate = useNavigate();

  // Estados principais
  const [liberacoes, setLiberacoes] = useState<ILiberacaoPadrao[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filtros
  const [headerFilters, setHeaderFilters] = useState<Record<FilterKey, string>>({
    nomeLiberacao: "",
    sistema: "",
    patrocinio: "",
  });
  const debouncedFilterColumn = useCallback(
    debounce((column: FilterKey, value: string) => {
      setHeaderFilters((prev) => ({ ...prev, [column]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    [setHeaderFilters, setPage]
  );
  const filteredLiberacoes = useMemo(() => {
    return liberacoes.filter((liberacao) => {
      const nome = liberacao.nomeLiberacao?.toLowerCase() ?? "";
      const sistema = liberacao.sistema?.toLowerCase() ?? "";
      const patrocinio = liberacao.patrocinio?.toLowerCase() ?? "";
      const filtroNome = headerFilters.nomeLiberacao.trim().toLowerCase();
      const filtroSistema = headerFilters.sistema.trim().toLowerCase();
      const filtroPatrocinio = headerFilters.patrocinio.trim().toLowerCase();

      if (filtroNome && !nome.includes(filtroNome)) {
        return false;
      }
      if (filtroSistema && !sistema.includes(filtroSistema)) {
        return false;
      }
      if (filtroPatrocinio && !patrocinio.includes(filtroPatrocinio)) {
        return false;
      }
      return true;
    });
  }, [headerFilters, liberacoes]);

  // Modal de criação/edição completo
  const [modalForm, setModalForm] = useState<{
    isOpen: boolean;
    liberacaoId?: number;
  }>({ isOpen: false });

  // Modal de confirmação de exclusão
  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    id?: number;
  }>({ isOpen: false });

  // Opções de sistemas
  const sistemasOpcoes = [
    { value: "", label: "Todos" },
    { value: "CEM", label: "CEM" },
    { value: "CEM Smart", label: "CEM Smart" },
    { value: "CEM Mini", label: "CEM Mini" },
    { value: "ONE", label: "ONE" },
  ];

  useEffect(() => {
    // ========================================
    // 🔒 VALIDAÇÃO PREVENTIVA DE TOKEN JWT
    // ========================================
    // Previne erro 401 verificando token ANTES de fazer requisições
    
    // 1. Verificar se token existe
    const token = sessionStorage.getItem("@MOF:token");
    
    if (!token || token.trim() === "") {
      console.warn("⚠️ Token ausente - redirecionando para login");
      irParaLogin();
      return;
    }

    try {
      // 2. Decodificar e validar expiração do token
      const decoded = jwtDecode<any>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (decoded.exp && decoded.exp < currentTime) {
        console.warn("⚠️ Token expirado - redirecionando para login");
        sessionStorage.clear();
        irParaLogin();
        return;
      }
      
      // 3. Verificar permissões necessárias para esta página
      const roles = decoded.role ? decoded.role.split(",") : [];
      const hasPermission = roles.some((r: string) => 
        ["Liberacao", "ConsultaLiberacao", "Master"].includes(r.trim())
      );
      
      if (!hasPermission) {
        console.warn("⚠️ Sem permissão para acessar Liberações Padrão");
        openNotification({ 
          message: "Você não tem permissão para acessar esta página", 
          type: "error" 
        });
        navigate("/dashboardMof");
        return;
      }
      
      // ✅ Token válido e permissões OK - carregar dados
      carregarLiberacoes();
      
    } catch (error) {
      // Token malformado ou erro ao decodificar
      console.error("❌ Erro ao validar token:", error);
      sessionStorage.clear();
      irParaLogin();
      return;
    }
  }, []);

  const carregarLiberacoes = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/ListaLiberacao");

      const lista: ILiberacaoPadrao[] = (Array.isArray(data) ? data : data?.items || []).map(
        (item: any) => ({
          id:
            item.id ??
            item.Id ??
            item.idLiberacaoPadraoCEM ??
            item.IdLiberacaoPadraoCEM ??
            0,
          nomeLiberacao:
            item.nomeLiberacao ??
            item.descricao ??
            item.Descricao ??
            item.empresa ??
            item.Empresa ??
            "-",
          sistema: item.sistema ?? item.Sistema ?? "-",
          patrocinio: item.patrocinio ?? item.Patrocinio ?? "Sem Patrocínio",
        }),
      );

      setLiberacoes(lista);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
      setLiberacoes([]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleRemoverLiberacao = async () => {
    if (!modalDelete.id) return;

    setIsLoading(true);
    try {
      await apiAntigo.delete(`/LiberacaoPadraoCEM/${modalDelete.id}`);
      openNotification({ message: "Liberação padrão excluída com sucesso!", type: "success" });
      setModalDelete({ isOpen: false });
      carregarLiberacoes();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "nomeLiberacao",
      headerName: "Nome da Liberação",
      flex: 2,
      minWidth: 250,
      renderHeader: () => (
        <FilterColumn
          label="Nome da Liberação"
          name="nomeLiberacao"
          onChange={(event) =>
            debouncedFilterColumn("nomeLiberacao", event.target.value)
          }
        />
      ),
    },
    {
      field: "sistema",
      headerName: "Sistema",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Sistema"
          name="sistema"
          onChange={(event) =>
            debouncedFilterColumn("sistema", event.target.value)
          }
        />
      ),
    },
    {
      field: "patrocinio",
      headerName: "Patrocínio",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Patrocínio"
          name="patrocinio"
          onChange={(event) =>
            debouncedFilterColumn("patrocinio", event.target.value)
          }
        />
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            color="primary"
            title="Editar"
            onClick={() =>
              setModalForm({ isOpen: true, liberacaoId: params.row.id })
            }
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Excluir"
            onClick={() =>
              setModalDelete({ isOpen: true, id: params.row.id })
            }
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />
      
      {/* Header conforme padrão CEM DELPHI */}
      <NavigationHeader
        title="LIBERAÇÕES PADRÃO CEM"
        label="Home > Cadastros"
        hasBackButton={true}
        goBack={() => navigate("/liberacoesMof")}
        buttonLabel="ADICIONAR"
        buttonOnClick={() => setModalForm({ isOpen: true })}
      />

      <Paper elevation={3}>
        {/* Texto descritivo conforme padrão CEM DELPHI */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "#f5f5f5" }}>
          <Typography variant="body2" color="textSecondary">
            Lista de liberações padrão cadastradas para o CEM
          </Typography>
        </Box>

        {/* Tabela */}
        <Box sx={{ p: 3 }}>
          <StripedDataGrid
            columns={columns}
            rows={filteredLiberacoes}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={filteredLiberacoes.length}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            checkboxSelection={false}
          />
        </Box>
      </Paper>

      {/* Modal completo de Criação/Edição */}
      <ModalLiberacaoPadrao
        open={modalForm.isOpen}
        onClose={() => setModalForm({ isOpen: false })}
        liberacaoId={modalForm.liberacaoId}
        onSave={carregarLiberacoes}
        nomesExistentes={liberacoes
          .filter((l) => l.id !== modalForm.liberacaoId)
          .map((l) => l.nomeLiberacao)}
      />

      <ConfirmDeleteModal
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={handleRemoverLiberacao}
        title="Excluir Liberação Padrão"
        description="Excluir esta liberação padrão fará com que novas licenças deixem de recebê-la automaticamente. Confirmar?"
        isLoadingDelete={isLoading}
      />
    </DashboardStackCustom>
  );
};

